import { createSelector } from 'reselect';
import { BIKES_PER_PAGE } from 'src/constants/bikes';
import sortOptions from 'src/config/sortOptions.config';

export const activeFilterSelector = createSelector(
  state => state.bikes.activeFilterId,
  activeFilterId => activeFilterId
);

export const getBikeFilter = filterKey =>
  createSelector(
    state => state.bikes.filters[filterKey],
    bikeFilter => bikeFilter
  );

export const getBikeFilterFrom = filterKey =>
  createSelector(
    state => state.bikes.filters[filterKey].from,
    from => from
  );

export const getBikeFilterTo = filterKey =>
  createSelector(
    state => state.bikes.filters[filterKey].to,
    to => to
  );

export const getFilters = createSelector(
  state => state.bikes.filters,
  filters => filters
);

export const isFilterSet = filterIndex =>
  createSelector(
    state => state.bikes.filters[filterIndex],
    filter => {
      if (filter.min) {
        return filter.min !== filter.from || filter.max !== filter.to;
      }

      if (filter.choice) {
        return Boolean(filter.choice);
      }

      return false;
    }
  );

export const areFiltersSet = createSelector(
  state => state.bikes.filters,
  filters =>
    Object.values(filters).some(filter => {
      if (typeof filter.min !== 'undefined') {
        return filter.min !== filter.from || filter.max !== filter.to;
      }

      if (typeof filter.choice !== 'undefined') {
        return Boolean(filter.choice);
      }

      return false;
    })
);

export const getFiltersOpen = createSelector(
  state => state.bikes.filtersOpen,
  filtersOpen => filtersOpen
);

export const getBikesView = createSelector(
  state => state.bikes.view,
  view => view
);

export const getFilterChoices = createSelector(
  state => state.bikes.filters,
  filters =>
    Object.values(filters).reduce((prevFilterChoices, filter) => {
      if (!filter.choice) {
        return prevFilterChoices;
      }

      return [...prevFilterChoices, filter.choice];
    }, [])
);

export const getPriceFilter = createSelector(
  state => state.app.data.filters,
  state => state.bikes.filters,
  (filters, bikeFilters) => {
    const priceFilter = filters.find(filter => filter.type === 'slider');

    if (!priceFilter) {
      return null;
    }

    const priceFilterIndex = filters.indexOf(priceFilter);

    return {
      ...priceFilter,
      ...bikeFilters[priceFilterIndex],
    };
  }
);

const filterBikes = filterChoices => bike => {
  if (!filterChoices.length) {
    return true;
  }

  return bike.tags && filterChoices.every(tag => bike.tags.includes(tag));
};

const filterBikesByPrice = filter => bike =>
  !filter || (bike.price >= filter.from && bike.price <= filter.to) || !bike.price;

export const bikesSelector = createSelector(
  state => state.bikes.results,
  state => state.bikes.page,
  state => state.bikes.sort,
  getFilterChoices,
  getPriceFilter,
  (bikes, page, sort, filterChoices, priceFilter) => {
    const selectedSortOption = sortOptions.find(sortOption => sortOption.id === sort);

    const filteredResults = bikes
      .filter(filterBikes(filterChoices))
      .filter(filterBikesByPrice(priceFilter))
      .sort(selectedSortOption ? selectedSortOption.sortFunction : () => 0);

    const resultsVisible = filteredResults.slice(0, page * BIKES_PER_PAGE);

    return {
      count: bikes.length,
      filteredCount: filteredResults.length,
      results: resultsVisible,
    };
  }
);

export const getFeatureCards = type =>
  createSelector(
    state => state.bikes[type === 'genre' ? 'genreCards' : 'familyCards'],
    state => state.bikes.sort,
    getFilterChoices,
    getPriceFilter,
    (features, sort, filterChoices, priceFilter) => {
      const selectedSortOption = sortOptions.find(sortOption => sortOption.id === sort);

      return Object.entries(features).reduce(
        (prevFeatures, [featureId, cards]) => ({
          ...prevFeatures,
          [featureId]: cards
            .filter(filterBikes(filterChoices))
            .filter(filterBikesByPrice(priceFilter))
            .sort(selectedSortOption ? selectedSortOption.sortFunction : () => 0),
        }),
        {}
      );
    }
  );

export const getFeatureCardCount = type =>
  createSelector(getFeatureCards(type), filteredFeatures => {
    if (!Object.entries(filteredFeatures).length) {
      return null;
    }

    return Object.entries(filteredFeatures).reduce((prevCount, [featureId, cards]) => prevCount + cards.length, 0);
  });

export const getBikesSort = createSelector(
  state => state.bikes.sort,
  sort => sort
);
